import { useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, LogOut, Sparkles, ChevronDown } from "lucide-react";
import { NAV } from "./constants";
import { useAuth } from "./AuthContext";
import { UpgradeProvider } from "./UpgradeContext";
import { UpgradeBanner } from "./components/UpgradeBanner";
import { UpgradeModal } from "./components/UpgradeModal";

const Brand = () => (
  <Link to="/credit/app" className="flex items-center gap-2.5 px-2">
    <div className="flex h-9 w-9 items-center justify-center rounded-xl cc-gradient-br cc-glow-blue">
      <Sparkles size={18} className="text-white" />
    </div>
    <div className="leading-tight">
      <div className="cc-heading text-[15px] font-600 text-white">ArcaCentum</div>
      <div className="text-[10px] tracking-[0.2em] uppercase text-blue-300/80">Credit</div>
    </div>
  </Link>
);

const NavList = ({ onNavigate }) => (
  <nav className="flex-1 overflow-y-auto cc-scroll px-3 py-4 space-y-0.5">
    {NAV.map((item, i) =>
      item.section ? (
        <div key={`s-${i}`} className="px-3 pt-5 pb-1.5 text-[10px] tracking-[0.2em] uppercase text-slate-600">
          {item.section}
        </div>
      ) : (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onClick={onNavigate}
          data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          className={({ isActive }) =>
            `cc-nav-item flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm ${
              isActive ? "cc-nav-active" : "text-slate-400"
            }`
          }
        >
          <item.icon size={17} strokeWidth={1.8} />
          {item.label}
        </NavLink>
      )
    )}
  </nav>
);

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const initials = (user?.name || user?.email || "U").slice(0, 1).toUpperCase();

  const doLogout = () => {
    logout();
    navigate("/credit/login");
  };

  return (
    <UpgradeProvider>
    <div className="cc cc-bg min-h-screen text-slate-200">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col cc-glass border-r border-white/[0.06] z-30">
        <div className="h-16 flex items-center border-b border-white/[0.06] px-4">
          <Brand />
        </div>
        <NavList />
        <div className="border-t border-white/[0.06] p-3">
          <button
            data-testid="sidebar-logout"
            onClick={doLogout}
            className="cc-nav-item flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-slate-400"
          >
            <LogOut size={17} /> Log out
          </button>
        </div>
      </aside>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              className="lg:hidden fixed inset-0 bg-black/60 z-40"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setMobileOpen(false)}
            />
            <motion.aside
              className="lg:hidden fixed inset-y-0 left-0 w-72 flex flex-col cc-surface border-r border-white/[0.06] z-50"
              initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }}
              transition={{ ease: [0.22, 1, 0.36, 1], duration: 0.3 }}
            >
              <div className="h-16 flex items-center justify-between border-b border-white/[0.06] px-4">
                <Brand />
                <button onClick={() => setMobileOpen(false)} className="text-slate-400 p-2">
                  <X size={20} />
                </button>
              </div>
              <NavList onNavigate={() => setMobileOpen(false)} />
              <div className="border-t border-white/[0.06] p-3">
                <button onClick={doLogout} className="cc-nav-item flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-slate-400">
                  <LogOut size={17} /> Log out
                </button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main */}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 h-16 flex items-center justify-between gap-4 px-4 sm:px-6 cc-glass border-b border-white/[0.06]">
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden text-slate-300 p-2 -ml-2"
              onClick={() => setMobileOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu size={22} />
            </button>
            <span className="hidden sm:inline text-sm text-slate-500">
              Welcome back{user?.name ? `, ${user.name.split(" ")[0]}` : ""}
            </span>
          </div>

          <div className="flex items-center gap-3">
            <Link
              to="/credit/app/billing"
              className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-xs text-slate-300 hover:text-white cc-nav-item"
              data-testid="topbar-plan"
            >
              Plan: <span className="text-blue-300 capitalize">{user?.plan || "free"}</span>
            </Link>
            <div className="relative">
              <button
                onClick={() => setMenuOpen((v) => !v)}
                className="flex items-center gap-2 rounded-full cc-surface-2 pl-1 pr-2.5 py-1 border border-white/10"
                data-testid="user-menu-btn"
              >
                <span className="flex h-7 w-7 items-center justify-center rounded-full cc-gradient text-white text-xs font-600">
                  {initials}
                </span>
                <ChevronDown size={14} className="text-slate-400" />
              </button>
              <AnimatePresence>
                {menuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                    className="absolute right-0 mt-2 w-52 cc-card rounded-xl p-2 z-30"
                    onMouseLeave={() => setMenuOpen(false)}
                  >
                    <div className="px-3 py-2 border-b border-white/[0.06] mb-1">
                      <div className="text-sm text-white truncate">{user?.name}</div>
                      <div className="text-xs text-slate-500 truncate">{user?.email}</div>
                    </div>
                    <Link to="/credit/app/settings" onClick={() => setMenuOpen(false)} className="block rounded-lg px-3 py-2 text-sm text-slate-300 cc-nav-item">Settings</Link>
                    <button onClick={doLogout} className="w-full text-left rounded-lg px-3 py-2 text-sm text-red-300 cc-nav-item">Log out</button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </header>

        <UpgradeBanner />

        <main className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto">
          <Outlet />
        </main>

        <footer className="px-4 sm:px-6 lg:px-8 pb-6 max-w-[1400px] mx-auto w-full" data-testid="app-footer">
          <div className="border-t border-white/[0.06] pt-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-[11px] text-slate-500">
            <span>© {new Date().getFullYear()} ArcaCentum, Inc.</span>
            <div className="flex gap-4">
              <Link to="/help" target="_blank" className="hover:text-slate-300">Help</Link>
              <Link to="/privacy" target="_blank" className="hover:text-slate-300">Privacy</Link>
              <Link to="/terms" target="_blank" className="hover:text-slate-300">Terms</Link>
              <Link to="/refund-policy" target="_blank" className="hover:text-slate-300">Refunds</Link>
              <a href="mailto:support@arcacentum.com" className="hover:text-slate-300">Support</a>
            </div>
          </div>
        </footer>
      </div>
      <UpgradeModal />
    </div>
    </UpgradeProvider>
  );
}
